/**
 * Subscription Admin Agent.
 *
 * Provides admin-only hooks for listing users/subscription plans
 * and assigning one or more plans to a user.
 */

import { useState, useEffect, useCallback } from 'react';
import APIAgent from './apiAgent';

const ENDPOINT = '/api/admin/subscriptions';

export function useAdminSubscriptions() {
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(ENDPOINT, 'GET');
      setPlans(Array.isArray(data?.plans) ? data.plans : []);
      setUsers(Array.isArray(data?.users) ? data.users : []);
      setIsAdmin(true);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('403') || msg.toLowerCase().includes('admin')) {
        setIsAdmin(false);
      } else {
        setError(msg || 'Failed to load subscriptions');
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const assignSubscription = useCallback(
    async (userId, subscriptionPlanCodes) => {
      const result = await APIAgent.request(
        `${ENDPOINT}/users/${encodeURIComponent(userId)}`,
        'PUT',
        { subscription_plan_codes: subscriptionPlanCodes }
      );
      await refresh();
      return result;
    },
    [refresh]
  );

  return {
    plans,
    users,
    loading,
    error,
    isAdmin,
    refresh,
    assignSubscription,
  };
}
